/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// allow-any-unicode-comment-file
// DO NOT EDIT -- auto-generated by scripts/sync-agent-host-protocol.ts

// Generated from types/actions.ts — do not edit
// Run `npm run generate` to regenerate.

import { ActionType, type StateAction, type RootAgentsChangedAction, type RootActiveSessionsChangedAction, type RootTerminalsChangedAction, type RootConfigChangedAction, type SessionReadyAction, type SessionCreationFailedAction, type SessionTurnStartedAction, type SessionDeltaAction, type SessionResponsePartAction, type SessionToolCallStartAction, type SessionToolCallDeltaAction, type SessionToolCallReadyAction, type SessionToolCallConfirmedAction, type SessionToolCallCompleteAction, type SessionToolCallResultConfirmedAction, type SessionToolCallContentChangedAction, type SessionTurnCompleteAction, type SessionTurnCancelledAction, type SessionErrorAction, type SessionTitleChangedAction, type SessionUsageAction, type SessionReasoningAction, type SessionModelChangedAction, type SessionAgentChangedAction, type SessionServerToolsChangedAction, type SessionActiveClientChangedAction, type SessionActiveClientToolsChangedAction, type SessionPendingMessageSetAction, type SessionPendingMessageRemovedAction, type SessionQueuedMessagesReorderedAction, type SessionInputRequestedAction, type SessionInputAnswerChangedAction, type SessionInputCompletedAction, type SessionCustomizationsChangedAction, type SessionCustomizationToggledAction, type SessionCustomizationUpdatedAction, type SessionTruncatedAction, type SessionIsReadChangedAction, type SessionIsArchivedChangedAction, type SessionActivityChangedAction, type SessionChangesetsChangedAction, type SessionConfigChangedAction, type SessionMetaChangedAction, type ChangesetStatusChangedAction, type ChangesetFileSetAction, type ChangesetFileRemovedAction, type ChangesetOperationsChangedAction, type ChangesetClearedAction, type TerminalDataAction, type TerminalInputAction, type TerminalResizedAction, type TerminalClaimedAction, type TerminalTitleChangedAction, type TerminalCwdChangedAction, type TerminalExitedAction, type TerminalClearedAction, type TerminalCommandDetectionAvailableAction, type TerminalCommandExecutedAction, type TerminalCommandFinishedAction } from './actions.js';


// ─── Root vs Session vs Terminal vs Changeset Action Unions ─────────────────

/** Union of all root-scoped actions. */
export type RootAction =
	| RootAgentsChangedAction
	| RootActiveSessionsChangedAction
	| RootTerminalsChangedAction
	| RootConfigChangedAction
	;

/** Union of root actions that clients may dispatch. */
export type ClientRootAction =
	| RootConfigChangedAction
	;

/** Union of root actions that only the server may produce. */
export type ServerRootAction =
	| RootAgentsChangedAction
	| RootActiveSessionsChangedAction
	| RootTerminalsChangedAction
	;

/** Union of all session-scoped actions. */
export type SessionAction =
	| SessionReadyAction
	| SessionCreationFailedAction
	| SessionTurnStartedAction
	| SessionDeltaAction
	| SessionResponsePartAction
	| SessionToolCallStartAction
	| SessionToolCallDeltaAction
	| SessionToolCallReadyAction
	| SessionToolCallConfirmedAction
	| SessionToolCallCompleteAction
	| SessionToolCallResultConfirmedAction
	| SessionToolCallContentChangedAction
	| SessionTurnCompleteAction
	| SessionTurnCancelledAction
	| SessionErrorAction
	| SessionTitleChangedAction
	| SessionUsageAction
	| SessionReasoningAction
	| SessionModelChangedAction
	| SessionAgentChangedAction
	| SessionServerToolsChangedAction
	| SessionActiveClientChangedAction
	| SessionActiveClientToolsChangedAction
	| SessionPendingMessageSetAction
	| SessionPendingMessageRemovedAction
	| SessionQueuedMessagesReorderedAction
	| SessionInputRequestedAction
	| SessionInputAnswerChangedAction
	| SessionInputCompletedAction
	| SessionCustomizationsChangedAction
	| SessionCustomizationToggledAction
	| SessionCustomizationUpdatedAction
	| SessionTruncatedAction
	| SessionIsReadChangedAction
	| SessionIsArchivedChangedAction
	| SessionActivityChangedAction
	| SessionChangesetsChangedAction
	| SessionConfigChangedAction
	| SessionMetaChangedAction
	;

/** Union of session actions that clients may dispatch. */
export type ClientSessionAction =
	| SessionTurnStartedAction
	| SessionToolCallConfirmedAction
	| SessionToolCallCompleteAction
	| SessionToolCallResultConfirmedAction
	| SessionToolCallContentChangedAction
	| SessionTurnCancelledAction
	| SessionTitleChangedAction
	| SessionModelChangedAction
	| SessionAgentChangedAction
	| SessionActiveClientChangedAction
	| SessionActiveClientToolsChangedAction
	| SessionPendingMessageSetAction
	| SessionPendingMessageRemovedAction
	| SessionQueuedMessagesReorderedAction
	| SessionInputAnswerChangedAction
	| SessionInputCompletedAction
	| SessionCustomizationToggledAction
	| SessionTruncatedAction
	| SessionIsReadChangedAction
	| SessionIsArchivedChangedAction
	| SessionConfigChangedAction
	;

/** Union of session actions that only the server may produce. */
export type ServerSessionAction =
	| SessionReadyAction
	| SessionCreationFailedAction
	| SessionDeltaAction
	| SessionResponsePartAction
	| SessionToolCallStartAction
	| SessionToolCallDeltaAction
	| SessionToolCallReadyAction
	| SessionTurnCompleteAction
	| SessionErrorAction
	| SessionUsageAction
	| SessionReasoningAction
	| SessionServerToolsChangedAction
	| SessionInputRequestedAction
	| SessionCustomizationsChangedAction
	| SessionCustomizationUpdatedAction
	| SessionActivityChangedAction
	| SessionChangesetsChangedAction
	| SessionMetaChangedAction
	;

/** Union of all terminal-scoped actions. */
export type TerminalAction =
	| TerminalDataAction
	| TerminalInputAction
	| TerminalResizedAction
	| TerminalClaimedAction
	| TerminalTitleChangedAction
	| TerminalCwdChangedAction
	| TerminalExitedAction
	| TerminalClearedAction
	| TerminalCommandDetectionAvailableAction
	| TerminalCommandExecutedAction
	| TerminalCommandFinishedAction
	;

/** Union of terminal actions that clients may dispatch. */
export type ClientTerminalAction =
	| TerminalInputAction
	| TerminalResizedAction
	| TerminalClaimedAction
	| TerminalTitleChangedAction
	| TerminalClearedAction
	;

/** Union of terminal actions that only the server may produce. */
export type ServerTerminalAction =
	| TerminalDataAction
	| TerminalCwdChangedAction
	| TerminalExitedAction
	| TerminalCommandDetectionAvailableAction
	| TerminalCommandExecutedAction
	| TerminalCommandFinishedAction
	;

/** Union of all changeset-scoped actions. */
export type ChangesetAction =
	| ChangesetStatusChangedAction
	| ChangesetFileSetAction
	| ChangesetFileRemovedAction
	| ChangesetOperationsChangedAction
	| ChangesetClearedAction
	;

/** Union of changeset actions that clients may dispatch. */
export type ClientChangesetAction =
	never
	;

/** Union of changeset actions that only the server may produce. */
export type ServerChangesetAction =
	| ChangesetStatusChangedAction
	| ChangesetFileSetAction
	| ChangesetFileRemovedAction
	| ChangesetOperationsChangedAction
	| ChangesetClearedAction
	;

// ─── Client-Dispatchable Map ─────────────────────────────────────────────────

/**
 * Exhaustive map indicating which action types may be dispatched by clients.
 * Adding a new action to StateAction without adding it here is a compile error.
 */
export const IS_CLIENT_DISPATCHABLE: { readonly [K in StateAction['type']]: boolean } = {
	[ActionType.RootAgentsChanged]: false,
	[ActionType.RootActiveSessionsChanged]: false,
	[ActionType.RootTerminalsChanged]: false,
	[ActionType.RootConfigChanged]: true,
	[ActionType.SessionReady]: false,
	[ActionType.SessionCreationFailed]: false,
	[ActionType.SessionTurnStarted]: true,
	[ActionType.SessionDelta]: false,
	[ActionType.SessionResponsePart]: false,
	[ActionType.SessionToolCallStart]: false,
	[ActionType.SessionToolCallDelta]: false,
	[ActionType.SessionToolCallReady]: false,
	[ActionType.SessionToolCallConfirmed]: true,
	[ActionType.SessionToolCallComplete]: true,
	[ActionType.SessionToolCallResultConfirmed]: true,
	[ActionType.SessionToolCallContentChanged]: true,
	[ActionType.SessionTurnComplete]: false,
	[ActionType.SessionTurnCancelled]: true,
	[ActionType.SessionError]: false,
	[ActionType.SessionTitleChanged]: true,
	[ActionType.SessionUsage]: false,
	[ActionType.SessionReasoning]: false,
	[ActionType.SessionModelChanged]: true,
	[ActionType.SessionAgentChanged]: true,
	[ActionType.SessionServerToolsChanged]: false,
	[ActionType.SessionActiveClientChanged]: true,
	[ActionType.SessionActiveClientToolsChanged]: true,
	[ActionType.SessionPendingMessageSet]: true,
	[ActionType.SessionPendingMessageRemoved]: true,
	[ActionType.SessionQueuedMessagesReordered]: true,
	[ActionType.SessionInputRequested]: false,
	[ActionType.SessionInputAnswerChanged]: true,
	[ActionType.SessionInputCompleted]: true,
	[ActionType.SessionCustomizationsChanged]: false,
	[ActionType.SessionCustomizationToggled]: true,
	[ActionType.SessionCustomizationUpdated]: false,
	[ActionType.SessionTruncated]: true,
	[ActionType.SessionIsReadChanged]: true,
	[ActionType.SessionIsArchivedChanged]: true,
	[ActionType.SessionActivityChanged]: false,
	[ActionType.SessionChangesetsChanged]: false,
	[ActionType.SessionConfigChanged]: true,
	[ActionType.SessionMetaChanged]: false,
	[ActionType.ChangesetStatusChanged]: false,
	[ActionType.ChangesetFileSet]: false,
	[ActionType.ChangesetFileRemoved]: false,
	[ActionType.ChangesetOperationsChanged]: false,
	[ActionType.ChangesetCleared]: false,
	[ActionType.TerminalData]: false,
	[ActionType.TerminalInput]: true,
	[ActionType.TerminalResized]: true,
	[ActionType.TerminalClaimed]: true,
	[ActionType.TerminalTitleChanged]: true,
	[ActionType.TerminalCwdChanged]: false,
	[ActionType.TerminalExited]: false,
	[ActionType.TerminalCleared]: true,
	[ActionType.TerminalCommandDetectionAvailable]: false,
	[ActionType.TerminalCommandExecuted]: false,
	[ActionType.TerminalCommandFinished]: false,
};
